// src/components/IssueDetails.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  Button,
  useTheme,
  Tabs,
  Tab,
  Snackbar,
  Alert,
} from "@mui/material";
import { CallMerge as CallMergeIcon } from "@mui/icons-material";
import { Info as InfoIcon } from "@mui/icons-material";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import SyntaxHighlighter from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import Layout from "./Layout";
import DOMPurify from 'dompurify';
import CreatePullRequest from './CreatePullRequest';
import CodeDiffViewer from './CodeDiffViewer';
import PullRequestReview from './PullRequestReview';


const IssueDetails = () => {
  const { projectId, issueKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { message, textRange } = location.state || {};
  const theme = useTheme();
  const [issueDetails, setIssueDetails] = useState(null);
  const [ruleDetails, setRuleDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ruleLoading, setRuleLoading] = useState(false);
  const [aiFixLoading, setAiFixLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [fixedCode, setFixedCode] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const REPORT_API_HOST = process.env.REACT_APP_API_NEW_HOST_URL;
  const token = localStorage.getItem("access_token");

  useEffect(() => {
    const fetchIssueDetails = async () => {
      try {
        const response = await fetch(
          `${REPORT_API_HOST}/athrv/api/v1/github/issue-details?issueKey=${issueKey}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();
        setIssueDetails(data);
      } catch (error) {
        console.error("Error fetching issue details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchIssueDetails();
  }, [REPORT_API_HOST, issueKey, token]);

  const fetchRuleDetails = async () => {
    if (ruleDetails) return;
    
    setRuleLoading(true);
    try {
      const response = await fetch(
        `${REPORT_API_HOST}/athrv/api/v1/platform/sonarqube/athrv-issue-details?issueKey=${issueKey}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      setRuleDetails(data);
    } catch (error) {
      console.error("Error fetching rule details:", error);
    } finally {
      setRuleLoading(false);
    }
  };

  const handleAiAutoFix = async () => {
    if (!projectId || !issueKey || !token) {
      setSnackbar({
        open: true,
        message: 'Missing required parameters for AI Auto Fix',
        severity: 'error'
      });
      return;
    }

    setAiFixLoading(true);
    
    try {
      const response = await fetch(`${REPORT_API_HOST}/athrv/api/v1/github/auto-fix/fix-sonar-issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          jwt_access_token: token,
          project_id: projectId,
          issue_key: issueKey,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSnackbar({
          open: true,
          message: 'AI Auto Fix completed successfully!',
          severity: 'success'
        });
        console.log('AI Fix Response:', data);
        
        // Store the fixed code from the API response
        if (data.fixed_code) {
          setFixedCode(data.fixed_code);
          // Automatically switch to the AI Fix tab after successful fix
          setActiveTab(2);
        }
        
        // Optionally refresh the issue details after fix
        // You can uncomment the line below if you want to reload the issue details
        // window.location.reload();
        
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSnackbar({
          open: true,
          message: `AI Auto Fix failed: ${errorData.message || 'Unknown error'}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error calling AI Auto Fix:', error);
      setSnackbar({
        open: true,
        message: `AI Auto Fix failed: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setAiFixLoading(false);
    }
  };

  const handleComparePullRequest = () => {
    navigate(`/projects/${projectId}/issues/${issueKey}/pr-review`, {
      state: {
        originalCode: issueDetails,
        fixedCode: fixedCode,
        fileName: Array.isArray(issueDetails) && issueDetails[0]?.issue_details?.component ? 
          extractFilePath(issueDetails[0].issue_details.component) : 
          null,
        issueKey: issueKey // Add this line
      }
    });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handlePullRequestSuccess = (data) => {
    console.log('Pull Request created successfully:', data);
    // You can add any additional logic here after successful PR creation
  };

  const handlePullRequestError = (error) => {
    console.error('Pull Request creation failed:', error);
    // You can add any additional logic here after failed PR creation
  };

  const stripHtmlTags = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const generateCodeWithLineNumbers = (sources) => {
    if (!sources) return "";
    return sources
      .map((source) => {
        const { line, code } = source;
        const cleanCode = stripHtmlTags(code);
        return `${line}: ${cleanCode}`;
      })
      .join("\n");
  };

  const extractFilePath = (componentPath) => {
    if (!componentPath) return "";
    const colonIndex = componentPath.indexOf(":");
    return colonIndex !== -1 ? componentPath.substring(colonIndex + 1) : componentPath;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      fetchRuleDetails();
    }
  };

  const sanitizeHtml = (html) => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['p', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'strong', 'em', 'a', 'code', 'pre', 'br'],
      ALLOWED_ATTR: ['href', 'target', 'rel']
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!issueDetails) {
    return (
      <Typography variant="h6" sx={{ textAlign: "center", marginTop: "20px" }}>
        Issue details not found.
      </Typography>
    );
  }

  const componentKey = Object.keys(issueDetails)[0];
  const { sources, component } = issueDetails[componentKey] || {};
  const codeWithLineNumbers = generateCodeWithLineNumbers(sources);

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, position: "relative" }}>
          {/* AI Auto Fix Button - Top Right Corner */}
          <Box
            sx={{
              position: "absolute",
              top: 20,
              right: 20,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              className="athrv-dash-primary-button"
              onClick={handleAiAutoFix}
              disabled={aiFixLoading}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.mode === "dark"
                  ? "#fff"
                  : theme.palette.primary.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.grey[300],
                  color: theme.palette.grey[600],
                },
                minWidth: '120px',
              }}
            >
              {aiFixLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Fixing...
                </Box>
              ) : (
                'AI Auto Fix'
              )}
            </Button>
            {!aiFixLoading && (
              <Tooltip
                title="AI-powered automatic issue resolution"
                placement="left"
                arrow
              >
                <InfoIcon
                  sx={{
                    color: theme.palette.grey[500],
                    fontSize: 20,
                    cursor: "help",
                  }}
                />
              </Tooltip>
            )}
          </Box>

          <Typography variant="h5" sx={{ marginBottom: 2 }}>
            Issue Details
          </Typography>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Issue Detail" />
              <Tab label="Why This is an Issue" />
              <Tab label="AI Fix" disabled={!fixedCode} />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {activeTab === 0 ? (
            <>
              <Box
                sx={{
                  marginBottom: 1,
                  display: "flex",
                  gap: "20px",
                  flexWrap: "wrap",
                }}
              >
                {projectId && (
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    Project ID:{" "}
                    <span style={{ fontWeight: "normal" }}>{projectId}</span>
                  </Typography>
                )}
                {issueKey && (
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    Issue Key:{" "}
                    <span style={{ fontWeight: "normal" }}>{issueKey}</span>
                  </Typography>
                )}
              </Box>

              {Array.isArray(issueDetails) &&
                issueDetails.map((issue, index) => (
                  <Box
                    key={index}
                    sx={{
                      marginBottom: 1,
                      display: "flex",
                      gap: "20px",
                      flexWrap: "wrap",
                    }}
                  >
                    {issue.issue_details?.textRange?.startLine && (
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        Start Line:{" "}
                        <span style={{ fontWeight: "normal" }}>
                          {issue.issue_details.textRange.startLine}
                        </span>
                      </Typography>
                    )}
                    {issue.issue_details?.textRange?.endLine && (
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        End Line:{" "}
                        <span style={{ fontWeight: "normal" }}>
                          {issue.issue_details.textRange.endLine}
                        </span>
                      </Typography>
                    )}
                  </Box>
                ))}

              {/* Display Component from issue_details */}
              {Array.isArray(issueDetails) &&
                issueDetails.map((issue, index) => (
                  <Box
                    key={`component-${index}`}
                    sx={{
                      marginBottom: 1,
                      display: "flex",
                      gap: "20px",
                      flexWrap: "wrap",
                    }}
                  >
                    {issue.issue_details?.component && (
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        Component:{" "}
                        <span style={{ fontWeight: "normal" }}>
                          {extractFilePath(issue.issue_details.component)}
                        </span>
                      </Typography>
                    )}
                  </Box>
                ))}

              {/* Display Message from issue_details */}
              {Array.isArray(issueDetails) &&
                issueDetails.map((issue, index) => (
                  <Box
                    key={`message-${index}`}
                    sx={{
                      marginBottom: 1,
                      display: "flex",
                      gap: "20px",
                      flexWrap: "wrap",
                    }}
                  >
                    {issue.issue_details?.message && (
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        Message:{" "}
                        <span style={{ fontWeight: "normal" }}>
                          {issue.issue_details.message}
                        </span>
                      </Typography>
                    )}
                  </Box>
                ))}

              <Box
                sx={{
                  marginBottom: 1,
                  display: "flex",
                  gap: "20px",
                  flexWrap: "wrap",
                }}
              >
                {component?.longName && (
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    Component:{" "}
                    <span style={{ fontWeight: "normal" }}>
                      {component?.longName}
                    </span>
                  </Typography>
                )}
              </Box>
              {Array.isArray(issueDetails) &&
                issueDetails.map((issue, index) => {
                  const codeLines = issue.code_snippet?.code_lines || [];
                  const textRange = issue.issue_details?.textRange;

                  return (
                    <Box key={index} sx={{ marginBottom: 3 }}>
                      {codeLines.length > 0 && (
                        <>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: "bold", marginBottom: 1 }}
                          >
                            Code Snippet:
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#2e2e2e"
                                  : "#f5f5f5",
                              border: `1px solid ${theme.palette.mode === "dark" ? "#444" : "#ddd"
                                }`,
                              borderRadius: "4px",
                              overflow: "auto",
                              fontFamily: '"Courier New", Courier, monospace',
                              fontSize: "14px",
                              lineHeight: "1.5",
                            }}
                          >
                            {/* Line Numbers */}
                            <Box
                              sx={{
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "#3a3a3a"
                                    : "#efefef",
                                padding: "10px",
                                borderRight: `1px solid ${theme.palette.mode === "dark" ? "#555" : "#ddd"
                                  }`,
                                userSelect: "none",
                                textAlign: "left",
                              }}
                            >
                              {codeLines.map((line, i) => (
                                <Typography
                                  key={i}
                                  sx={{
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#bbb"
                                        : "#666",
                                    fontWeight: "normal",
                                    paddingRight: "10px",
                                  }}
                                >
                                  {line.line_number}
                                </Typography>
                              ))}
                            </Box>

                            {/* Code Lines */}
                            <Box
                              sx={{ padding: "10px", flex: 1, textAlign: "left" }}
                            >
                              {codeLines.map((line, i) => {
                                const isHighlighted =
                                  textRange &&
                                  line.line_number >= Number(textRange.startLine) &&
                                  line.line_number <= Number(textRange.endLine);
                                return (
                                  <Typography
                                    key={i}
                                    sx={{
                                      backgroundColor: isHighlighted
                                        ? theme.palette.mode === "dark"
                                          ? "#ff6666"
                                          : "#ffcccb"
                                        : "transparent",
                                      color: isHighlighted
                                        ? "#000"
                                        : theme.palette.mode === "dark"
                                          ? "#ddd"
                                          : "#333",
                                      fontWeight: isHighlighted ? "bold" : "normal",
                                      textAlign: "left",
                                      whiteSpace: "pre-wrap",
                                      wordWrap: "break-word",
                                    }}
                                  >
                                    {stripHtmlTags(line.code || "\n")}
                                  </Typography>
                                );
                              })}
                            </Box>
                          </Box>
                        </>
                      )}
                    </Box>
                  );
                })}
            </>
          ) : activeTab === 1 ? (
            <Box sx={{ p: 2 }}>
              {ruleLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                  }}
                >
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Loading rule details...</Typography>
                </Box>
              ) : ruleDetails?.rule_details?.rule_data ? (
                <Box>
                  {/* Rule Header Information */}
                  <Box sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: theme.palette.grey[50], 
                    borderRadius: 1, 
                    border: `1px solid ${theme.palette.grey[200]}` 
                  }}>
                    <Typography variant="h6" sx={{ 
                      mb: 1, 
                      fontWeight: "bold", 
                      color: theme.palette.primary.main 
                    }}>
                      Rule: {ruleDetails.rule_details?.rule_data?.name || 'Rule Description'}
                    </Typography>
                    {ruleDetails.rule_details?.rule_data?.key && (
                      <Typography variant="body2" sx={{ 
                        color: theme.palette.text.secondary, 
                        mb: 1 
                      }}>
                        {/* Rule ID: {ruleDetails.rule_details.athrv_rule_id} */}
                      </Typography>
                    )}
                    {ruleDetails.rule_details?.rule_data?.severity && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          Severity:
                        </Typography>
                        <Box 
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: 
                              ruleDetails.rule_details.rule_data.severity === 'MAJOR' ? '#ff9800' :
                              ruleDetails.rule_details.rule_data.severity === 'CRITICAL' ? '#f44336' :
                              ruleDetails.rule_details.rule_data.severity === 'MINOR' ? '#2196f3' :
                              ruleDetails.rule_details.rule_data.severity === 'BLOCKER' ? '#9c27b0' : '#4caf50',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {ruleDetails.rule_details.rule_data.severity}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* Rule Description Content */}
                  <Box 
                    sx={{
                      '& h2': {
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        margin: '1.5rem 0 1rem',
                        color: theme.palette.primary.main,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        paddingBottom: '0.5rem'
                      },
                      '& h3': {
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        margin: '1.25rem 0 0.75rem',
                        color: theme.palette.text.primary
                      },
                      '& p': {
                        margin: '1rem 0',
                        lineHeight: 1.6
                      },
                      '& ul, & ol': {
                        paddingLeft: '2rem',
                        margin: '1rem 0'
                      },
                      '& li': {
                        marginBottom: '0.5rem',
                        lineHeight: 1.5
                      },
                      '& code': {
                        backgroundColor: theme.palette.grey[100],
                        padding: '2px 4px',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.9em'
                      },
                      '& pre': {
                        backgroundColor: theme.palette.grey[100],
                        padding: '1rem',
                        borderRadius: '4px',
                        overflow: 'auto',
                        margin: '1rem 0',
                        '& code': {
                          backgroundColor: 'transparent',
                          padding: 0
                        }
                      },
                      '& a': {
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }
                    }}
                  >
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 3, 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.grey[200]}`
                      }}
                    >
                      {ruleDetails.rule_details.rule_data.descriptionSections?.length > 0 ? (
                        ruleDetails.rule_details.rule_data.descriptionSections.map((section, index) => (
                          <div 
                            key={index}
                            dangerouslySetInnerHTML={{ 
                              __html: sanitizeHtml(section.content)
                            }} 
                          />
                        ))
                      ) : (
                        <div 
                          dangerouslySetInnerHTML={{ 
                            __html: sanitizeHtml(
                              ruleDetails.rule_details.rule_data.htmlDesc || 
                              ruleDetails.rule_details.rule_data.mdDesc || 
                              'No rule description available.'
                            ) 
                          }} 
                        />
                      )}
                    </Paper>
                  </Box>

                  {/* Additional Rule Information */}
                  {(ruleDetails.rule_details?.rule_data?.tags?.length > 0 || 
                    ruleDetails.rule_details?.rule_data?.sysTags?.length > 0) && (
                    <Box sx={{ 
                      mt: 3, 
                      p: 2, 
                      backgroundColor: theme.palette.grey[50], 
                      borderRadius: 1 
                    }}>
                      <Typography variant="subtitle2" sx={{ 
                        mb: 1, 
                        fontWeight: 'bold' 
                      }}>
                        Tags:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {[...(ruleDetails.rule_details.rule_data.tags || []), 
                          ...(ruleDetails.rule_details.rule_data.sysTags || [])].map((tag, index) => (
                          <Box
                            key={index}
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              backgroundColor: theme.palette.primary.light,
                              color: theme.palette.primary.contrastText,
                              borderRadius: 2,
                              fontSize: '0.8rem'
                            }}
                          >
                            {tag}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : ruleDetails ? (
                <Typography variant="body1">
                  No rule description available.
                </Typography>
              ) : (
                <Typography variant="body1">
                  Failed to load rule details. Please try again.
                </Typography>
              )}
            </Box>
          ) : activeTab === 2 && fixedCode ? (
            <Box sx={{ p: 2 }}>
              <CodeDiffViewer 
                originalCode={issueDetails} 
                fixedCode={fixedCode}
                fileName={Array.isArray(issueDetails) && issueDetails[0]?.issue_details?.component ? 
                  extractFilePath(issueDetails[0].issue_details.component) : 
                  null
                }
              />
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body1" sx={{ textAlign: 'center', color: theme.palette.text.secondary }}>
                Click "AI Auto Fix" to generate a fix for this issue.
              </Typography>
            </Box>
          )}

          {/* Bottom Button Container */}
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: activeTab === 2 && fixedCode ? 'space-between' : 'flex-start', 
              alignItems: 'center', 
              marginTop: 3 
            }}
          >
            <Button
              variant="contained"
              className="athrv-dash-primary-button"
              onClick={() =>
                navigate(`/projects/${projectId}`, { state: { activeTab: 2 } })
              }
            >
              Back to Issue List
            </Button>

            {/* Create Pull Request Button - Only show in AI Fix tab when fixedCode exists */}
            {activeTab === 2 && fixedCode && (
              <Button
                variant="contained"
                onClick={handleComparePullRequest}
                sx={{
                  backgroundColor: theme.palette.success.main,
                  color: theme.palette.success.contrastText,
                  "&:hover": {
                    backgroundColor: theme.palette.success.dark,
                  },
                  minWidth: '160px',
                }}
                startIcon={<CallMergeIcon />}
              >
                Compare & Pull Request
              </Button>
            )}
          </Box>
        </Paper>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default IssueDetails;